import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { maskCnpj, maskCpf } from "@/lib/brdocs";

const STATUS = { pending: "Pendente", approved: "Aprovado", rejected: "Recusado" };

const addrText = (a) => (typeof a === "string" ? a : a ? `${a.street}, ${a.number}${a.complement ? `, ${a.complement}` : ""} — ${a.district}, ${a.city}/${a.state} · CEP ${a.cep}` : "—");

function Documents({ v }) {
  const [docs, setDocs] = useState(null);
  const load = async () => {
    setDocs("loading");
    try { setDocs((await api.get(`/admin/verifications/${v.id}/documents`)).data); } catch (e) { toast.error(formatApiError(e)); setDocs(null); }
  };
  if (!v.documents?.length) return <p className="mt-3 text-sm text-muted-foreground">Documentos: {v.document_link || "—"}</p>;
  if (!docs) return <Button size="sm" variant="secondary" className="mt-3 rounded-full" onClick={load} data-testid={`verification-docs-load-${v.id}`}><FileText size={14} className="mr-1.5" /> Ver {v.documents.length} documento(s)</Button>;
  if (docs === "loading") return <Loader2 className="mt-3 animate-spin text-muted-foreground" size={18} />;
  return (
    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={`verification-docs-${v.id}`}>
      {docs.map((d, i) => (
        <a key={i} href={d.url} target="_blank" rel="noreferrer" className="group overflow-hidden rounded-xl border border-white/10 bg-white/5" data-testid={`verification-doc-${v.id}-${i}`}>
          <img src={d.preview} alt={d.name} className="aspect-[4/3] w-full bg-white object-cover object-top" />
          <p className="flex items-center gap-1.5 p-2 text-xs font-semibold"><span className="truncate">{d.name}</span><span className="ml-auto shrink-0 uppercase text-muted-foreground">{d.format}</span><ExternalLink size={12} className="shrink-0" /></p>
        </a>
      ))}
    </div>
  );
}

function VerificationCard({ v, onDecide }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-card p-5" data-testid={`verification-item-${v.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-lg font-bold">{v.institution_name}</p>
        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${v.status === "pending" ? "bg-amber-500/15 text-amber-300" : v.status === "approved" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{STATUS[v.status]}</span>
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        {[["Razão social", v.legal_name], ["CNPJ", maskCnpj(v.cnpj)], ["Tradição", v.denomination === "catolica" ? "Católica" : "Evangélica"], ["Cidade", v.city], ["Endereço", addrText(v.address)], ["Líder", `${v.leader_name} (${v.leader_role})`], ["CPF do responsável", v.leader_cpf ? maskCpf(v.leader_cpf) : "—"], ["Telefone", v.contact_phone]].map(([k, val]) => (
          <div key={k} className="flex min-w-0 gap-2"><dt className="shrink-0 text-muted-foreground">{k}:</dt><dd className="min-w-0 break-words" title={val}>{val}</dd></div>
        ))}
      </dl>
      <Documents v={v} />
      {v.notes && <p className="mt-2 text-sm italic text-white/70">"{v.notes}"</p>}
      {v.status === "pending" && (
        <div className="mt-4 flex gap-2">
          <Button className="rounded-full bg-emerald-600 hover:bg-emerald-500" onClick={() => onDecide(v, true)} data-testid={`verification-approve-${v.id}`}>Conceder Selo</Button>
          <Button variant="secondary" className="rounded-full" onClick={() => onDecide(v, false)} data-testid={`verification-reject-${v.id}`}>Recusar</Button>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/verifications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (v, approve) => {
    try {
      await api.post(`/admin/verifications/${v.id}/decision`, { approve });
      toast.success(approve ? `Selo concedido a ${v.institution_name}` : "Solicitação recusada");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="py-6 md:py-10">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--gold))]">Administração</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="admin-title">Selos de Validação Institucional</h1>
      <div className="mt-8 space-y-3" data-testid="verification-list">
        {items.length === 0 && <p className="py-14 text-center text-muted-foreground">Nenhuma solicitação.</p>}
        {items.map((v) => <VerificationCard key={v.id} v={v} onDecide={decide} />)}
      </div>
    </div>
  );
}
